import React from 'react'
import { FiX } from 'react-icons/fi'
import { Icon } from '@chakra-ui/icons'
import { Image, Text } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { AspectRatio, Box, Wrap, WrapItem } from '@chakra-ui/react'

import { useTeamChatAttachments } from './provider'
import ImagePreviewModal from '~components/shared/modals/image-preview-modal'
import FullScreenPreview from '~components/chats/chat/messages-list/attachment-preview/full-screen-preview'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import {
    Attachment,
    AttachmentType,
    CreateInternalChatMessageAttachmentInput,
} from '~graphql/generated/graphql'
import { Center } from '@chakra-ui/react'
import { FaCirclePlay, FaFile } from 'react-icons/fa6'

export default function TeamChatInputAttachmentsPreview() {
    const { attachments } = useTeamChatAttachments()

    if (attachments.length === 0) return null

    return (
        <Wrap w="full" gridGap={4} px={2} mb={2}>
            {attachments.map(attachment => (
                <WrapItem key={attachment.filename}>
                    <AttachmentPreviewItem attachment={attachment} />
                </WrapItem>
            ))}
        </Wrap>
    )
}

function AttachmentPreviewItem({
    attachment,
}: {
    attachment: CreateInternalChatMessageAttachmentInput
}) {
    const { setAttachments, isProcessing } = useTeamChatAttachments()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const handleRemoveAttachment = () => {
        setAttachments(prev =>
            prev.filter(a => a.filename !== attachment.filename),
        )
    }

    const isPdf = attachment.type === AttachmentType.Pdf

    return (
        <>
            <Box position="relative" role="group" cursor="pointer">
                <AspectRatio
                    ratio={1}
                    w={16}
                    rounded="xl"
                    overflow="hidden"
                    borderWidth={1}
                    borderColor="gray.200"
                    bg={isPdf ? 'gray.100' : 'gray.200'}
                    _groupHover={{
                        filter: 'brightness(0.9)',
                    }}
                    transition="filter 0.1s ease-in-out"
                    onClick={onOpen}
                >
                    {attachment.type === AttachmentType.Pdf ? (
                        <PdfAttachment attachment={attachment} />
                    ) : attachment.type === AttachmentType.Video ? (
                        <VideoAttachment attachment={attachment} />
                    ) : (
                        <ImageAttachment attachment={attachment} />
                    )}
                </AspectRatio>
                <IconButtonWithTooltip
                    label="Remove"
                    tooltipOverrides={{
                        placement: 'top',
                    }}
                    icon={<Icon as={FiX} />}
                    size="xs"
                    borderWidth={2}
                    borderColor="gray.50"
                    bg="gray.200"
                    display="none"
                    _hover={{
                        bg: 'gray.300',
                    }}
                    _groupHover={{
                        display: 'flex',
                    }}
                    rounded="full"
                    variant="subtle"
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    onClick={handleRemoveAttachment}
                />
            </Box>
            {isPdf ? (
                <FullScreenPreview
                    attachment={
                        {
                            id: attachment.filename,
                            url: attachment.url,
                            filename: attachment.filename,
                            type: AttachmentType.Pdf,
                        } as Attachment
                    }
                    isOpen={isOpen}
                    onClose={onClose}
                    showDownload
                />
            ) : (
                <ImagePreviewModal
                    isOpen={isOpen}
                    onClose={onClose}
                    url={attachment.url}
                    type={attachment.type as AttachmentType}
                />
            )}
        </>
    )
}

function PdfAttachment({
    attachment,
}: {
    attachment: CreateInternalChatMessageAttachmentInput
}) {
    return (
        <Center flexDir="column" gridGap={1} px={1}>
            <Icon as={FaFile} fontSize="xl" color="gray.300" />
            <Text
                fontSize="9px"
                color="gray.400"
                fontWeight="medium"
                noOfLines={1}
            >
                {attachment.filename}
            </Text>
        </Center>
    )
}

function ImageAttachment({
    attachment,
}: {
    attachment: CreateInternalChatMessageAttachmentInput
}) {
    return (
        <Image
            src={attachment.url}
            alt={attachment.filename}
            w="full"
            h="full"
            objectFit="cover"
        />
    )
}

function VideoAttachment({
    attachment,
}: {
    attachment: CreateInternalChatMessageAttachmentInput
}) {
    return (
        <Box position="relative" w="full" h="full">
            <Box
                as="video"
                src={attachment.url}
                h="full"
                w="full"
                objectFit="cover"
            />
            <Center
                position="absolute"
                top={0}
                right={0}
                left={0}
                bottom={0}
                bg="blackAlpha.200"
            >
                <Icon as={FaCirclePlay} fontSize="2xl" color="whiteAlpha.700" />
            </Center>
        </Box>
    )
}
